import { useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
    acceptInvitationApiV1OrganizationsOrgIdMembersAcceptInvitePost,
    getPendingInvitationsApiV1OrganizationsOrgIdMembersPendingGet,
    inviteMemberApiV1OrganizationsOrgIdMembersInvitePost,
    revokeInvitationApiV1OrganizationsOrgIdInvitationsMembershipIdDelete,
    validateInvitationTokenApiV1OrganizationsInvitationsTokenValidateGet,
} from '@/common/api/client/sdk.gen'
import type { AcceptInviteRequest, InviteMemberRequest, SuccessResponse } from '@/common/api/client/types.gen'
import { useCurrentOrg, useChangeOrg } from '../../org/model'
import { useFormMutation } from '@/common/hooks/useFormMutation'
import { useMe } from '@/common/entities/me/model/me'

const getAllInvitations = async (org_id: string) => {
    const res = await getPendingInvitationsApiV1OrganizationsOrgIdMembersPendingGet({ path: { org_id } })
    return res.data!
}

const inviteInvitation = async (org_id: string, data: InviteMemberRequest) => {
    const res = await inviteMemberApiV1OrganizationsOrgIdMembersInvitePost({
        path: { org_id },
        body: data,
    })
    return res.data!
}

const validateInvitationToken = async (token: string) => {
    const res = await validateInvitationTokenApiV1OrganizationsInvitationsTokenValidateGet({ path: { token } })
    return res.data!
}

/** В OpenAPI для accept-invite в типах не указан path `org_id`, хотя он есть в URL */
const acceptInvitation = async (org_id: string, token: string) => {
    const res = await acceptInvitationApiV1OrganizationsOrgIdMembersAcceptInvitePost({
        path: { org_id },
        body: { token },
    } as unknown as Parameters<typeof acceptInvitationApiV1OrganizationsOrgIdMembersAcceptInvitePost>[0])
    return res.data!
}

const revokeInvitation = async (org_id: string, membership_id: string) => {
    const res = await revokeInvitationApiV1OrganizationsOrgIdInvitationsMembershipIdDelete({
        path: { org_id, membership_id },
    })
    return res.data!
}

export const useOrgInvitations = () => {
    const { data: currentOrg, isLoading: currentOrgLoading } = useCurrentOrg()

    const {
        data: orgInvitations,
        isLoading,
        error,
    } = useQuery({
        queryKey: ['org_invitations', currentOrg?.id],
        queryFn: () => getAllInvitations(currentOrg?.id ?? ''),
        enabled: !!currentOrg,
    })

    return { orgInvitations, currentOrg, isLoading: currentOrgLoading || isLoading, error }
}

export const useAddOrgInvitation = () => {
    const { data: currentOrg } = useCurrentOrg()

    const queryClient = useQueryClient()
    const currentOrgId = currentOrg?.id

    const { form, onSubmit, register, handleSubmit, isLoading, isError, error, isSuccess } = useFormMutation<
        InviteMemberRequest,
        SuccessResponse,
        {
            detail?: string
            field_errors?: Record<keyof InviteMemberRequest, string>
        }
    >({
        mutationFn: (data: InviteMemberRequest) => inviteInvitation(currentOrgId ?? '', data),
        toastOptions: {
            successMessage: 'Приглашение отправлено',
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['org_invitations', currentOrgId] })
        },
    })

    useEffect(() => {
        form.reset({
            email: '',
            role: 'admin',
        })
    }, [form])

    return { form, handleSubmit, onSubmit, register, isPending: isLoading, isSuccess, isError, error }
}

export const useInvitationInfo = (token: string) => {
    const { me } = useMe()

    const {
        data: invitationInfo,
        isLoading,
        error,
    } = useQuery({
        queryKey: ['org_invitation_info', token],
        queryFn: () => validateInvitationToken(token),
        enabled: !!token,
    })

    const isRegistrationForm =
        typeof me === 'undefined' && invitationInfo?.user_exists === false && invitationInfo?.user_registered === false
    const isSameUser = invitationInfo?.email === me?.email
    const isValidToken = invitationInfo?.is_valid && !invitationInfo?.is_expired

    return { invitationInfo, isSameUser, isValidToken, isRegistrationForm, isLoading: isLoading, error }
}

export const useAcceptInvitation = (org_id: string, token: string) => {
    const navigate = useNavigate()
    const changeOrg = useChangeOrg()

    const { mutation, handleSubmit, onSubmit, isLoading, isError, error, isSuccess, errors } = useFormMutation<
        AcceptInviteRequest,
        SuccessResponse,
        {
            detail?: string
            field_errors?: Record<keyof AcceptInviteRequest, string>
        }
    >({
        mutationFn: () => acceptInvitation(org_id, token),
        toastOptions: {
            successMessage: 'Приглашение принято',
        },
        onSuccess: async () => {
            await changeOrg(org_id)
            navigate('/app')
        },
    })

    return { mutation, handleSubmit, onSubmit, isLoading, isError, error, isSuccess, errors }
}

export const useRevokeInvitation = () => {
    const queryClient = useQueryClient()
    const { data: currentOrg } = useCurrentOrg()

    return useMutation({
        mutationFn: (membership_id: string) => revokeInvitation(currentOrg?.id ?? '', membership_id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['org_invitations', currentOrg?.id] })
        },
    })
}
